<template>

    <div class="prospective-client-page ">
        <div class="modal__logo-wrapper">
            <img src="../assets/icons/default/logo_main.svg" alt="logo" />
        </div>

        <div v-if="tokenChecked && !validToken" class="invalid-token">
            <p>{{ $t(`customerSignature.${"invalidTokenMsg"}`) }}</p>
            <img src="../assets/icons/default/stop.png" class="status-img" alt="stop-completed" />
        </div>

        <div v-else-if="validToken" class="modal__panel">
            <form class="form form--terminate" @submit.prevent="">
                <div class="navigators">
                    <div class="navigator navigator--active">
                        <div class="navigator__bullet"></div>
                        <div class="navigator__title">
                            {{ $t(`customerSignature.${"formTitle"}`) }} :
                        </div>
                    </div>
                </div>

                <div class="form__field">
                    <VueSignaturePad ref="signaturePad" class="signature-canvas" />

                    <div class="form__error" v-if="signatureError">
                        {{ $t(`customerSignature.${"signatureErrorMsg"}`) }}
                    </div>
                </div>

                <div class="form__buttons">
                    <button type="button" class="button button--default" @click="clearSignature">{{
                        $t(`customerSignature.${"clearBtn"}`) }} </button>
                    <div v-if="isRequesting" class="button button--default button--loading"></div>
                    <button v-else type="button" class="button button--default" @click="submitAll">{{
                        $t(`customerSignature.${"submitBtn"}`) }} </button>
                </div>
            </form>
        </div>

        <div v-if="completed" class="completed-text">
            <p>{{ $t(`customerSignature.${"successMsg"}`) }}</p>
            <img src="../assets/icons/default/completed.png" class="status-img" alt="logo-completed" />
        </div>

    </div>
</template>

<script>
import api from "@/api";
import { mapGetters } from "vuex";
export default {
    data() {
        return {
            validToken: false,
            tokenChecked: false,

            signatureError: false,
            isRequesting: false,

            completed: false,
        };
    },
    computed: {
        ...mapGetters(["isDarkMode"]),
    },
    mounted() {
        this.setLanguageFromParams();
        this.validateParams();
        this.applyTheme();
    },
    watch: {
        '$route.query.language': {
            handler() {
                this.setLanguageFromParams();
            },
            immediate: true
        },
        isDarkMode: {
            handler() {
                this.applyTheme();
            },
            immediate: true
        }
    },
    methods: {
        applyTheme() {
            document.documentElement.setAttribute(
                "data-theme",
                this.isDarkMode ? "dark" : "light"
            );
        },
        setLanguageFromParams() {
            const lang = this.$route.query.language;
            const validLanguages = ['de', 'en', 'it', 'fr'];
            
            if (lang && validLanguages.includes(lang)) {
                this.$i18n.locale = lang;
                this.$root.$i18n.locale = lang;
                // Override the App.vue's language setting
                window.localStorage.setItem("language", lang);
                this.$store.commit("setLanguage", lang);
            }
        },
        async validateParams() {
            this.completed = false;

            const signature = this.$route.query.signature;
            const id = this.$route.query.id;
            const expires = this.$route.query.expires;
            const submitSignature = this.$route.query.submitSignature;

            const currentTime = Math.floor(Date.now() / 1000);

            if (!signature || !id || !submitSignature || !expires || parseInt(expires) <= currentTime) {
                this.validToken = false;
                this.tokenChecked = true;
                return;
            }

            try {
                const response = await api({
                    requiresAuth: false
                }).get(
                    `/mandate-signature/${id}/verify`, {
                    params: {
                        signature: signature,
                        expires: expires
                    }
                }
                );

                this.validToken = response.status === 200;

            } catch (error) {
                this.validToken = false;
            } finally {
                this.tokenChecked = true;
            }
        },
        clearSignature() {
            this.$refs.signaturePad.clearSignature();
        },
        saveSignature() {
            this.$refs.signaturePad.saveSignature();
        },

        base64ToFile(base64String, filename) {
            return new Promise((resolve, reject) => {
                fetch(base64String)
                    .then(res => res.blob())
                    .then(blob => {
                        const file = new File([blob], filename, {
                            type: "image/png"
                        });
                        resolve(file);
                    })
                    .catch(error => reject(error));
            });
        },

        async submitAll() {
            this.isRequesting = true;

            try {
                this.signatureError = false;

                if (this.$refs.signaturePad.isEmpty()) {
                    this.signatureError = true;
                    this.isRequesting = false;

                    this.$store.commit("setToast", {
                        display: true,
                        type: "error",
                        message: this.$t(`customerSignature.${"signatureValMsg"}`)
                    });

                    return;
                }

                const {
                    data
                } = this.$refs.signaturePad.saveSignature();
                const file = await this.base64ToFile(data, "signature.png");

                const idParam = this.$route.query.id;
                const signatureParam = this.$route.query.submitSignature;
                const expiresParam = this.$route.query.expires;

                const url =
                    `/mandate-signature/${idParam}/submit?signature=${signatureParam}&expires=${expiresParam}`;

                const formData = new FormData();
                formData.append("file", file);

                await api({
                    requiresAuth: true
                }).post(url, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                });

                this.$store.commit("setToast", {
                    display: true,
                    type: "success",
                    message: this.$t(`customerSignature.${"successMsg"}`)
                });

                this.validToken = false;
                this.tokenChecked = false;
                this.completed = true;

            } catch (error) {
                const backendMessage = error?.response?.data?.message || error.message || "Unknown error";

                this.$store.commit("setToast", {
                    display: true,
                    type: "error",
                    message: this.$t(`customerSignature.errorMsg`) + ": " + backendMessage
                });
            }

            this.isRequesting = false;
        }
    },
}
</script>

<style scoped>
.prospective-client-page {
    position: relative;
    max-width: 600px;
    margin: 0 auto;
    margin-top: 250px;
    background-color: var(--bg-page, #ffffff);
    min-height: 100vh;
    transition: background-color 0.3s ease;
}

.modal__logo-wrapper {
    top: -200px;
}

.invalid-token {
    font-size: 24px;
    text-align: center;
    color: var(--color-error, red);
    font-weight: bold;
}

.completed-text {
    font-size: 24px;
    text-align: center;
    color: var(--color-success, green);
    font-weight: bold;
}

.status-img {
    width: 150px;
    height: 150px;
    margin: 0 auto;
    margin-top: 20px;
}

.modal__panel {
    margin-top: 50px !important;
    padding: 0 !important;
    width: 100% !important;
    height: 450px !important;
    background-color: var(--bg-panel, #ffffff);
    transition: background-color 0.3s ease;
}

.signature-canvas {
    margin-top: 20px;
    margin-bottom: 60px;
    border-radius: 10px;
    box-shadow: 0px 7px 18px var(--shadow-2);
    background-color: var(--bg-canvas, #ffffff);
    transition: background-color 0.3s ease;
}

.form__error {
    bottom: -35px;
    font-size: 14px;
    color: var(--color-error, red);
}

.form__buttons {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.button {
    margin: 0 auto;
    width: 200px;
}

/* Dark mode styles */
[data-theme="dark"] .prospective-client-page {
    --bg-page: #181E25;
    --bg-panel: #313B47;
    --bg-canvas: #46505B;
    --color-error: #ff6b6b;
    --color-success: #51cf66;
    color: #ffffff;
}

[data-theme="dark"] .modal__panel {
    box-shadow: 0px 7px 18px rgba(0, 0, 0, 0.5);
}

[data-theme="dark"] .signature-canvas {
    box-shadow: 0px 7px 18px rgba(0, 0, 0, 0.5);
}

[data-theme="dark"] .navigator__title {
    color: #ffffff;
}

@media(max-width:950px) {
    .modal__logo-wrapper {
        position: relative !important;
        width: 150px !important;
        top: 0 !important;
        left: 0 !important;
        transform: none !important;
        margin: 0 auto;
    }

    .prospective-client-page {
        margin-top: 0;
        padding: 50px 15px;
        height: 100%;
        max-width: initial;
        width: 100% !important;
        overflow: scroll;
    }

    .modal__panel {
        height: auto !important;
    }
}

@media(max-width:678px) {
    .modal__panel {
        width: 90% !important;
        margin-left: auto !important;
        margin-right: auto !important;
    }

    .button {
        margin: 0 !important;
        width: 150px !important;
    }
}

@media(max-width:540px) {
    .signature-canvas {
        height: 200px !important;
    }
}
</style>
